import { Box, Checkbox, Chip, IconButton, InputAdornment, Typography } from '@mui/material';
import { ClearIcon } from 'components/icons/ClearIcon';
import { SearchIconMUI } from 'components/icons/SearchIconMUI';
import SearchNotFound from 'components/SearchNotFound';
import Select, {
  type ClearIndicatorProps,
  type ControlProps,
  components,
  type GroupBase,
  type MenuListProps,
  type OptionProps,
  type PlaceholderProps,
  type Props,
} from 'react-select';
import { SelectedValuesContainer, useCustomStyles } from './styles';

const Control = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  children,
  ...props
}: ControlProps<Option, IsMulti, Group>) => (
  <components.Control {...props}>
    <InputAdornment position="start" sx={{ ml: 1.5 }}>
      <SearchIconMUI color="disabled" />
    </InputAdornment>
    {children}
  </components.Control>
);

const Option = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  children,
  ...props
}: OptionProps<Option, IsMulti, Group>) => (
  <components.Option {...props}>
    {props.isMulti && (
      <Checkbox checked={props.isSelected} disabled={props.isDisabled} size="small" />
    )}
    {children}
  </components.Option>
);

const MenuList = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  children,
  ...props
}: MenuListProps<Option, IsMulti, Group>) => {
  const { getValue, selectProps, selectOption } = props;
  const selectedValues = getValue();
  const hasSearchQuery = selectProps.inputValue && selectProps.inputValue.trim().length > 0;
  const shouldDisplaySelectedValues = selectedValues.length > 0 && !hasSearchQuery && props.isMulti;

  return (
    <components.MenuList {...props}>
      {shouldDisplaySelectedValues && (
        <SelectedValuesContainer>
          {selectedValues.map((option) => {
            const isOptionDisabled =
              selectProps.isOptionDisabled?.(option, selectedValues) ?? false;

            return (
              <Chip
                key={selectProps.getOptionValue?.(option)}
                label={selectProps.getOptionLabel?.(option)}
                onDelete={isOptionDisabled ? undefined : () => selectOption(option)}
                color="primary"
                size="small"
              />
            );
          })}
        </SelectedValuesContainer>
      )}
      {children}
    </components.MenuList>
  );
};

const ClearIndicator = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: ClearIndicatorProps<Option, IsMulti, Group>
) => (
  <components.ClearIndicator {...props}>
    <IconButton size="small" sx={{ padding: '4px' }}>
      <ClearIcon fontSize="small" />
    </IconButton>
  </components.ClearIndicator>
);

const Placeholder = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  children,
  ...props
}: PlaceholderProps<Option, IsMulti, Group>) => {
  const selectedValues = props.getValue();

  if (props.isMulti && selectedValues.length === 1) {
    const firstValue = props.selectProps.getOptionLabel(selectedValues[0]) || '';

    return (
      <components.SingleValue {...props} data={selectedValues[0]}>
        {firstValue}
      </components.SingleValue>
    );
  }

  return <components.Placeholder {...props}>{children}</components.Placeholder>;
};

export interface ReactSelectAutocompleteProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> extends Props<Option, IsMulti, Group> {
  helperText?: string;
  error?: boolean;
  isSearchFilteringHandledExternally?: boolean;
  onMenuChange?: (isOpen: boolean) => void;
}

export const ReactSelectAutocomplete = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  helperText,
  error = false,
  isSearchFilteringHandledExternally = true,
  onMenuChange,
  ...props
}: ReactSelectAutocompleteProps<Option, IsMulti, Group>) => {
  const customStyles = useCustomStyles<Option, IsMulti, Group>(error);
  const isMulti = props.isMulti ?? false;

  return (
    <Box sx={{ width: '100%' }} data-testid="react-select-autocomplete">
      <Select
        controlShouldRenderValue={!isMulti}
        closeMenuOnSelect={!isMulti}
        backspaceRemovesValue={false}
        hideSelectedOptions={false}
        isClearable
        menuPortalTarget={document.body}
        menuPosition="fixed"
        filterOption={isSearchFilteringHandledExternally ? null : undefined}
        components={{
          Control,
          MenuList,
          Option,
          ClearIndicator,
          Placeholder,
          DropdownIndicator: null,
          IndicatorSeparator: null,
        }}
        styles={customStyles}
        noOptionsMessage={() => <SearchNotFound />}
        onMenuOpen={() => onMenuChange?.(true)}
        onMenuClose={() => onMenuChange?.(false)}
        {...props}
        formatOptionLabel={(option, optionLabelData) => {
          return optionLabelData.context === 'value' || !props.formatOptionLabel
            ? props.getOptionLabel?.(option)
            : props.formatOptionLabel?.(option, optionLabelData);
        }}
      />
      {helperText && (
        <Typography
          variant="caption"
          color={error ? 'error' : 'text.secondary'}
          sx={{ mt: 0.5, display: 'block' }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};
